import React, { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { PageHeader, ROLE_LABELS } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Check, UploadCloud, Loader2, BadgeCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PILLAR_LABEL = { surveillance: "Surveillance", laboratory: "Laboratoire", rcce: "CREC / RCCE", eds: "EDS / SDB", ipc: "PCI", case_mgmt: "Prise en Charge" };

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-900 text-right">{value || "-"}</span>
  </div>
);

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, lang, setLang } = useLang();
  const [form, setForm] = useState({ phone: user?.phone || "", job_title: user?.job_title || "", project: user?.project || "", point_focal: user?.point_focal || "" });
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("token");

  const choose = async (l) => {
    setLang(l);
    try { await api.post("/auth/language", { language: l }); setUser({ ...user, language: l }); toast.success(t("Langue mise à jour.")); } catch (e) { /* non-blocking */ }
  };

  const saveProfile = async () => {
    setBusy(true);
    try { const res = await api.patch("/auth/profile", form); setUser(res.data); toast.success(t("Profil mis à jour.")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.patch("/auth/profile", { photo_path: up.data.path });
      setUser(res.data); toast.success(t("Photo mise à jour."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const LANGS = [["fr", t("Français")], ["en", t("Anglais")]];
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");

  return (
    <div className="max-w-2xl mx-auto" data-testid="profile-page">
      <PageHeader eyebrow="Mon compte" title={t("Mon Profil")} subtitle={t("Identité de l'agent et préférences du compte")} />

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4 flex items-center gap-5">
        {user?.photo_path
          ? <img src={`${BACKEND}/api/mr-files/${user.photo_path}?auth=${token}`} alt="" className="h-20 w-20 rounded-lg object-cover bg-slate-100" data-testid="profile-photo" />
          : <div className="h-20 w-20 rounded-lg bg-[#0F172A] text-white flex items-center justify-center text-xl font-bold">{initials}</div>}
        <div className="flex-1">
          <p className="text-xl font-bold text-slate-900">{user?.name}</p>
          <p className="text-sm text-slate-500">{t(ROLE_LABELS[user?.role] || user?.role)}{user?.pillar ? ` · ${t(PILLAR_LABEL[user.pillar] || user.pillar)}` : ""}</p>
          <label className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#F05A28] cursor-pointer hover:underline" data-testid="profile-photo-upload">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} {t("Changer la photo")}
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={busy} />
          </label>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4">
        <div className="flex items-center gap-2 mb-2"><BadgeCheck className="h-4 w-4 text-[#F05A28]" /><h3 className="text-sm font-semibold text-slate-800">{t("Identité & affectation")}</h3></div>
        <Row label={t("Matricule")} value={user?.matricule} />
        <Row label={t("Adresse e-mail")} value={user?.email} />
        <Row label={t("Organisation")} value={user?.organization} />
        <Row label={t("Province")} value={user?.province} />
        <Row label={t("Zone de Santé")} value={user?.health_zone} />
        <p className="text-xs text-slate-500 mt-3">{t("Le rôle, le matricule et l'affectation sont assignés par l'administrateur.")}</p>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">{t("Informations pour le rapport")}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">{t("Téléphone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pf-phone" /></div>
          <div><Label className="text-xs">{t("Fonction / poste")}</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="pf-job" /></div>
          <div><Label className="text-xs">{t("Projet")}</Label><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} data-testid="pf-project" /></div>
          <div><Label className="text-xs">{t("Point focal / superviseur")}</Label><Input value={form.point_focal} onChange={(e) => setForm({ ...form, point_focal: e.target.value })} data-testid="pf-pf" /></div>
        </div>
        <Button className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B] mt-5" onClick={saveProfile} disabled={busy} data-testid="pf-save">{t("Enregistrer le profil")}</Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">{t("Langue de l'interface")}</h3>
        <p className="text-xs text-slate-500 mb-4">{t("Ce choix est mémorisé sur votre compte et appliqué à chaque connexion.")}</p>
        <div className="grid grid-cols-2 gap-3">
          {LANGS.map(([code, name]) => (
            <button key={code} onClick={() => choose(code)} data-testid={`profile-lang-${code}`}
              className={`flex items-center justify-between rounded-md border px-4 py-3 text-left transition-colors ${lang === code ? "border-[#F05A28] bg-[#FFF3EE]" : "border-slate-200 hover:bg-slate-50"}`}>
              <span className="text-sm font-medium text-slate-800">{name} <span className="uppercase text-xs text-slate-500 ml-1">{code}</span></span>
              {lang === code && <Check className="h-4 w-4 text-[#F05A28]" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
